import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Snackbar, Alert } from '@mui/material';
import api from '../services/api';
import surveyApi from '../services/surveyApi';
import { processSQDData, processSQDDataByOffice } from '../utils/sqdUtils';

// Layout Components
import { SideNavigation, Header, FilterDialog } from '../components/layout';

// Tab Components
import { DashboardTab } from '../components/dashboard';
import { FeedbackTab } from '../components/feedback';
import { ReportsTab } from '../components/reports'
import { UserManagementTab } from '../components/users';
import { SettingsTab } from '../components/settings';
import { YearlyReportTab } from '../components/yearlyReport';

// Utils
import { satisfactionToNumeric } from '../components/common/constants';

const AdminDashboard = () => {
  const navigate = useNavigate();

  // State for controlling tabs
  const [tabValue, setTabValue] = useState(0);
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // State for filters
  const [dateRange, setDateRange] = useState('last30');
  const [activeFilters, setActiveFilters] = useState({});
  
  // State for filter dialog
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [isFiltered, setIsFiltered] = useState(false);

  const [totalCount, setTotalCount] = useState(0);
  const [percentageSatisfaction, setPercentageSatisfaction] = useState(0);
  const [averageTimeSpent, setAverageTimeSpent] = useState(0);

  

  // Check authentication first
  useEffect(() => {
  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    
    if (!token || !user) {
      navigate('/login');
      return;
    }
    
    // Verify token is valid
    try {
      const response = await api.get('/user');
      console.log('User authenticated:', response.data);

      await fetchFeedbacks();
    
      // ... any other data fetching
      
    } catch (error) {
      console.error('Auth check error:', error);
      // If unauthorized, redirect to login
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
      }
    }
  };
  
  checkAuth();
}, [navigate]);

  const handleLogout = () => {
    // Call the API to logout
    api.post('/logout')
      .then(() => {
        // Clear local storage
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Redirect to login
        navigate('/login');
      })
      .catch((error) => {
        console.error('Logout error:', error);
        // Still clear token and redirect even if API call fails
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
      });
  };

  useEffect(() => {
    // If we have active custom filters, don't fetch based on dateRange change
    if (!isFiltered) {
      fetchFeedbacks();
    }
  }, [dateRange, isFiltered]);

  const fetchFeedbacks = (customFilters = null) => {
    setLoading(true);
    
    if (customFilters && Object.keys(customFilters).length > 0) {
      const params = { ...customFilters };
      
      console.log('Fetching with custom filters:', params);
      
      surveyApi.getSurvey(params)
        .then(response => {
          
          if (response && response.surveys) {
            const newData = response.surveys.data || response.surveys;
            console.log(`Received ${newData.length} records with filters`);
            setFeedbacks(newData);
            setIsFiltered(true);
            
            setTotalCount(response.totalCount || 0);
            setPercentageSatisfaction(response.percentageSatisfaction || 0);
            setAverageTimeSpent(response.averageTimeSpent || 0);
  
            console.log(`Total count: ${response.totalCount || 0}`);
            console.log(`Percentage satisfaction: ${response.percentageSatisfaction || 0}`);
            console.log(`Average time spent: ${response.averageTimeSpent || 0}`);
          } else {
            showSnackbar('Error loading filtered surveys', 'error');
          }
          setLoading(false);
        })
        .catch(error => {
          console.error('Error fetching filtered surveys:', error);
          handleFetchError(error);
          setLoading(false);
        });
    } else {
      // Use date range from dropdown
      const dateParams = getDateRangeParams(dateRange);
      
      console.log('Fetching with date range:', dateParams);
      
      surveyApi.getSurveyWithDateRange(dateParams.startDate, dateParams.endDate)
        .then(response => {
          if (response.data.success) {
            const newData = response.data.data.data || response.data.data;
            console.log(`Received ${newData.length} records with date range`);
            setFeedbacks(newData);
            setIsFiltered(false);
            
           // Set total count
           const totalCount = response.data.data.total || response.data.total || 0;
           setTotalCount(totalCount);
           const percentageSatisfaction = response.data.percentageSatisfaction || 0;
           setPercentageSatisfaction(percentageSatisfaction);
           const averageTimeSpent = response.data.averageTimeSpent || 0;
           setAverageTimeSpent(averageTimeSpent);
  
           console.log(`Total count: ${totalCount}`);
           console.log(`Percentage satisfaction: ${percentageSatisfaction}`);
           console.log(`Average time spent: ${averageTimeSpent}`);
          } else {
            showSnackbar('Error loading surveys', 'error');
          }
          setLoading(false);
        })
        .catch(error => {
          console.error('Error fetching surveys:', error);
          handleFetchError(error);
          setLoading(false);
        });
    }
  };
  

  useEffect(() => {
    // If we have active custom filters, don't fetch based on dateRange change
    if (!isFiltered) {
      fetchFeedbacks(); // Add this line to fetch office ratings when date range changes
    }
  }, [dateRange, isFiltered]);
  
  // Handle API errors, especially auth errors
  const handleFetchError = (error) => {
    if (error.response && error.response.status === 401) {
      showSnackbar('Your session has expired. Please log in again.', 'error');
      setTimeout(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
      }, 2000);
    } else {
      showSnackbar('Failed to load surveys', 'error');
    }
  };
  
  // Helper to get date range parameters
  const getDateRangeParams = (range) => {
    const endDate = new Date();
    let startDate = new Date();
    
    switch (range) {
      case 'last7':
        startDate.setDate(endDate.getDate() - 7);
        break;
      case 'last30':
        startDate.setDate(endDate.getDate() - 30);
        break;
      case 'last90':
        startDate.setDate(endDate.getDate() - 90);
        break;
      case 'thisYear':
        startDate = new Date(endDate.getFullYear(), 0, 1); // January 1st of current year
        break;
      case 'allTime':
        startDate = new Date(2000, 0, 1); // Far back enough to include all data
        break;
      default:
        startDate.setDate(endDate.getDate() - 30); // Default to last 30 days
    }
    
    return {
      startDate: formatDate(startDate),
      endDate: formatDate(endDate)
    };
  };
  
  // Helper to format date
  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };
  
  // Handle applying custom filters from dialog
  const handleApplyFilters = (filters) => {
    console.log('Applying filters:', filters);
    
    // First, make sure we have actual filter values (not empty objects or null values)
    const hasRealFilters = Object.entries(filters).some(([key, value]) => 
      value !== null && value !== undefined && value !== '' && value !== 'all'
    );
    
    if (hasRealFilters) {
      setActiveFilters(filters);
      setIsFiltered(true);
      fetchFeedbacks(filters);
    } else {
      // If no real filters are provided, clear filters
      handleClearFilters();
    }
  };
  
  
  // Handle clearing filters
  const handleClearFilters = () => {
    console.log('Clearing all filters');
    setActiveFilters({});
    setIsFiltered(false);
    fetchFeedbacks(null);
    showSnackbar('All filters cleared', 'info');
  };
  
  // Function to show snackbar
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  // Handle filter dialog
  const handleOpenFilterDialog = () => {
    setFilterDialogOpen(true);
  };
  
  // Handle date range change
  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
    setIsFiltered(false); // Reset filtered state
    setActiveFilters({}); // Clear any active filters
  };
  
  // Mock data for dashboard
  const dashboardStats = {
    totalRespondents: totalCount,
    avgSatisfactionRate: percentageSatisfaction,
    averageTimeToComplete: averageTimeSpent
  };
  
  const serviceBreakdownData = [
    { name: 'Records', value: 35 },
    { name: 'Personnel', value: 25 },
    { name: 'Cash', value: 20 },
    { name: 'Procurement', value: 15 },
    { name: 'Legal', value: 5 }
  ];
  
  const clientTypeData = [
    { name: 'Citizen', value: 58 },
    { name: 'Government', value: 32 },
    { name: 'Business', value: 10 }
  ];
  
  const sqdAnalysisData = useMemo(() => 
    processSQDData(feedbacks), [feedbacks]);
  
  // Calculate SQD scores
  const calculateSQDScores = () => {
    const scores = {
      sqd0: 0, sqd1: 0, sqd2: 0, sqd3: 0, sqd4: 0, 
      sqd5: 0, sqd6: 0, sqd7: 0, sqd8: 0, sqd9: 0, sqd10: 0
    };
    
    const counts = {
      sqd0: 0, sqd1: 0, sqd2: 0, sqd3: 0, sqd4: 0, 
      sqd5: 0, sqd6: 0, sqd7: 0, sqd8: 0, sqd9: 0, sqd10: 0
    };
    
    // Make sure feedbacks is an array before trying to iterate
    if (Array.isArray(feedbacks)) {
      feedbacks.forEach(feedback => {
        Object.keys(scores).forEach(key => {
          if (feedback[key] !== 'N/A') {
            scores[key] += satisfactionToNumeric(feedback[key]);
            counts[key]++;
          }
        });
      });
    }

    
    
    const avgScores = {};
    Object.keys(scores).forEach(key => {
      avgScores[key] = counts[key] > 0 ? scores[key] / counts[key] : 0;
    });
    
    return avgScores;
  };

  
  
  // Use useMemo to compute sqdScores only when feedbacks change
  const sqdScores = useMemo(() => calculateSQDScores(), [feedbacks]);
  
  // Prepare SQD data for chart
  const sqdChartData = useMemo(() => [
    { name: 'SQD0', score: sqdScores.sqd0 },
    { name: 'SQD1', score: sqdScores.sqd1 },
    { name: 'SQD2', score: sqdScores.sqd2 },
    { name: 'SQD3', score: sqdScores.sqd3 },
    { name: 'SQD4', score: sqdScores.sqd4 },
    { name: 'SQD5', score: sqdScores.sqd5 },
    { name: 'SQD6', score: sqdScores.sqd6 },
    { name: 'SQD7', score: sqdScores.sqd7 },
    { name: 'SQD8', score: sqdScores.sqd8 },
    { name: 'SQD9', score: sqdScores.sqd9 },
    { name: 'SQD10', score: sqdScores.sqd10 }
  ], [sqdScores]);
  
  // Prepare report data for export
  const reportData = {
    serviceBreakdownData,
    clientTypeData,
    sqdChartData
  };

  

  // If still loading initial auth check, show nothing
  if (loading && feedbacks.length === 0) {
    return null; // Or a loading spinner
  }

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Side Navigation */}
      <SideNavigation tabValue={tabValue} setTabValue={setTabValue} handleLogout={handleLogout} />
      
      {/* Main Content Area */}
      <Box sx={{ pl: 2, flexGrow: 1, ml: 30, mr: 3, mt: 2, mb: 4 }}>
        {/* Header */}
        <Header 
          tabValue={tabValue} 
          dateRange={dateRange} 
          setDateRange={handleDateRangeChange} 
          handleOpenFilterDialog={handleOpenFilterDialog} 
          refreshData={() => fetchFeedbacks(isFiltered ? activeFilters : null)}
          sqdScores={sqdScores}
          reportData={reportData}
          isFiltered={isFiltered}
          onClearFilters={handleClearFilters}
        />
        
        {/* Tab Content */}
        {tabValue === 0 && (
        <DashboardTab 
          dashboardStats={dashboardStats}
          serviceBreakdownData={serviceBreakdownData}
          sqdChartData={sqdAnalysisData.chartData}
          clientTypeData={clientTypeData}
          sqdScores={sqdAnalysisData.scores}
          loading={loading}
          dateRange={dateRange}
          isFiltered={isFiltered}
          activeFilters={activeFilters}
        />
        )}
        
        {tabValue === 1 && (
          
          <FeedbackTab 
          feedbackData={feedbacks}
          loading={loading}
          onDeleteSuccess={() => fetchFeedbacks(isFiltered ? activeFilters : null)}
          activeFilters={activeFilters}
          onClearFilters={handleClearFilters}
          isFiltered={isFiltered}
        />
        )}
        
        {tabValue === 2 && (
          <ReportsTab 
          dashboardStats={dashboardStats}
            clientTypeData={clientTypeData}
            serviceBreakdownData={serviceBreakdownData}
            sqdChartData={sqdAnalysisData.chartData}
          sqdScores={sqdAnalysisData.scores}
          overallSQDScore={sqdAnalysisData.overallScore}
          />
        )}
        
        {tabValue === 3 && (
          <UserManagementTab />
        )}
        
        {tabValue === 4 && (
          <SettingsTab />
        )}

        {tabValue === 5 && (
          <YearlyReportTab />
        )}

        <Snackbar 
          open={snackbar.open} 
          autoHideDuration={6000} 
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
        
        {/* Dialogs */}
        <FilterDialog 
          open={filterDialogOpen} 
          onClose={() => setFilterDialogOpen(false)}
          onApplyFilters={handleApplyFilters}
          currentFilters={activeFilters}
          feedbackCount={feedbacks.length}
          showSnackbar={showSnackbar}
        />
      </Box>
    </Box>
  );
};

export default AdminDashboard;